<script setup>
/* eslint semi: 1 */
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { FwbButton, FwbProgress, FwbSpinner } from 'flowbite-vue';
import { useFileUploaderProgress } from '@/composables/file-upload/use-file-uploader-progress';
import { TARGET_COMPONENTS } from '@/composables/file-upload/use-file-uploader';
import CONST from '@/globals/constants';

/** Maps media types to their display icons */
const MEDIA_TYPE_ICON_MAP = new Map([
  [CONST.MEDIA_TYPE_VALUE_IMAGE, 'flowbite:image-solid'],
  [CONST.MEDIA_TYPE_VALUE_VIDEO, 'flowbite:video-camera-solid'],
  [CONST.MEDIA_TYPE_VALUE_AUDIO, 'flowbite:headphones-solid'],
  [CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST, 'flowbite:list-music-solid'],
  [CONST.MEDIA_TYPE_VALUE_PDF, 'flowbite:file-pdf-solid'],
  [CONST.MEDIA_TYPE_VALUE_ELEMENTI, 'flowbite:file-zip-solid'],
  [CONST.MEDIA_TYPE_VALUE_PROJECT, 'flowbite:file-solid'],
]);

const props = defineProps({
  /** Array of component types whose file uploads should be displayed */
  targetComponents: {
    type: Array,
    required: true,
    validator: (value) => value.every(v => Object.values(TARGET_COMPONENTS).includes(v))
  }
});

const uploaderProgress = useFileUploaderProgress();
const { t } = useI18n();

/** Maps component types to their display labels */
const COMPONENT_GROUP_DISPLAY_NAMES = new Map([
  [TARGET_COMPONENTS.FILES_PAGE, t('label.filesPage')],
  [TARGET_COMPONENTS.LED_PAGE, t('label.ledPage')],
  [TARGET_COMPONENTS.VIDEOWALL_PAGE, t('label.videowallPage')],
  [TARGET_COMPONENTS.ALERT_PAGE, t('label.alertPage')],
  ['unknown', null],
]);

const isPopupExpanded = computed(() => uploaderProgress.isPopupVisible.value);
const allActiveUploadsDone = computed(() => uploaderProgress.allActiveUploadsDone.value);
const allActiveUploadsSucceeded = computed(() => uploaderProgress.allActiveUploadsSucceeded.value);
const allActiveUploadsFailed = computed(() => uploaderProgress.allActiveUploadsFailed.value);
const someActiveUploadsFailed = computed(() => {
  if (!allActiveUploadsDone.value) return false;
  return !allActiveUploadsSucceeded.value && !allActiveUploadsFailed.value;
});
const totalUploadsCount = computed(() => uploaderProgress.activeUploads.value.length);
const succeededUploadsCount = computed(() => uploaderProgress.activeUploads.value.filter((file) => [
  CONST.FILE_UPLOAD_ITEM_STATUS.FINISH,
  CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED
].includes(file.status)).length);

/**
 * Enhances active uploads with display properties (name and icon).
 * Extracts media name from upload metadata and determines appropriate icon.
 * Falls back to filename if media details are unavailable.
 */
const displayableActiveUploads = computed(() => {
  return uploaderProgress.activeUploads.value.map((file) => {
    // Try to get media details from either full details or create response
    const mediaDetails = file._edit?.getMediaFullDetails?.media ??
      file._edit?.uploadRequests?.createMediaInstanceResponseData;

    let displayStatus = file?.status;
    if ([CONST.FILE_UPLOAD_ITEM_STATUS.FINISH, CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED].includes(file?.status)) {
      displayStatus = CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED;
    } else if ([CONST.FILE_UPLOAD_ITEM_STATUS.FAILED, CONST.FILE_UPLOAD_ITEM_STATUS.CANCEL].includes(file?.status)) {
      displayStatus = CONST.FILE_UPLOAD_ITEM_STATUS.FAILED;
    }

    return {
      ...file,
      displayName: mediaDetails?.name ?? file?.name,
      displayIcon: MEDIA_TYPE_ICON_MAP.get(mediaDetails?.type) ?? 'flowbite:file-solid',
      displayStatus
    };
  });
});

/**
 * Groups uploads by their target component type.
 *
 * Organizes uploads into logical sections for better UX:
 * - Recognized components: grouped by FILES_PAGE, LED_PAGE, ALERT_PAGE
 * - Unrecognized/missing component info: grouped under 'unknown'
 *
 * Returns object: { [componentType]: [file1, file2, ...] }
 */
const uploadsByComponent = computed(() => {
  const groupedByComponent = {};

  displayableActiveUploads.value.forEach((file) => {
    const componentType = file._edit?.targetComponent;

    // Handle unrecognized or missing component type
    if (!componentType || !COMPONENT_GROUP_DISPLAY_NAMES.has(componentType)) {
      if (!groupedByComponent['unknown']) {
        groupedByComponent['unknown'] = [];
      }
      groupedByComponent['unknown'].push(file);
      return;
    }

    // Add file to its component group
    if (!groupedByComponent[componentType]) {
      groupedByComponent[componentType] = [];
    }
    groupedByComponent[componentType].push(file);
  });

  return groupedByComponent;
});

onMounted(() => {
  // Register this component's target components with the progress tracker
  uploaderProgress.setTargetComponents(props.targetComponents);
});
</script>

<template>
  <div v-if="displayableActiveUploads.length">
    <div
      class="file-uploader-progress-container relative flex min-w-sm rounded-lg border border-gray-200 shadow-md bg-white w-[343px] max-h-[398px] p-4 overflow-hidden"
      data-component-element-id="file-uploader-progress-container"
    >
      <div class="flex flex-col gap-4 flex-1 w-full">
        <div class="flex items-center gap-2 flex-shrink-0">
          <div v-if="!isPopupExpanded && !allActiveUploadsDone" class="w-8 h-8 p-1 bg-primary-100 rounded-lg">
            <FwbSpinner size="6" />
          </div>

          <h5
            class="text-sm font-bold text-gray-900 dark:text-white flex-1 text-start"
            data-component-element-id="file-uploader-progress-title"
          >
            <template v-if="allActiveUploadsSucceeded">{{ $t('media.mediaIsReady') }}</template>
            <template v-else-if="allActiveUploadsFailed">{{ $t('media.failedUploadErrorTitle') }}</template>
            <template v-else-if="someActiveUploadsFailed">
              {{ $t('media.mediaIsReadyWithCount', { count: succeededUploadsCount, total: totalUploadsCount }) }}
            </template>
            <template v-else>{{ $t('media.mediaUploadSuccessTitle') }}</template>
          </h5>

          <FwbButton
            v-if="allActiveUploadsDone"
            data-component-element-id="file-uploader-progress-close-btn"
            type="button"
            color="light"
            size="xs"
            pill
            square
            class="border-none"
            @click="uploaderProgress.dismissPopUp()"
          >
            <BaseIconSvg
              name="flowbite:close-outline"
              class="w-4! h-4!"
            />
          </FwbButton>

          <FwbButton
            v-else
            data-component-element-id="file-uploader-progress-collapse-btn"
            type="button"
            color="light"
            size="xs"
            pill
            square
            class="border-none"
            @click="uploaderProgress.setPopupVisible(!isPopupExpanded)"
          >
            <BaseIconSvg
              name="flowbite:angle-up-outline"
              class="w-4! h-4!"
              :class="{ 'rotate-180': isPopupExpanded }"
            />
          </FwbButton>
        </div>

        <BaseNotificationAlertsItem
          v-if="allActiveUploadsDone && !allActiveUploadsFailed"
          notifId="file-uploader-progress-info"
          type="info"
          :message="$t('media.mediaFileUploadInfoMessage')"
          class="flex-shrink-0"
        />

        <BaseNotificationAlertsItem
          v-if="allActiveUploadsFailed"
          notifId="file-uploader-progress-error"
          type="danger"
          :message="$t('media.failedUploadError')"
          class="flex-shrink-0"
        />
        <BaseNotificationAlertsItem
          v-else-if="someActiveUploadsFailed"
          notifId="file-uploader-progress-error"
          type="danger"
          :message="$t('media.someFilesUploadError')"
          class="flex-shrink-0"
        />

        <transition
          :duration="150"
          enter-from-class="translate-y-full"
          enter-to-class="translate-y-0"
          leave-from-class="translate-y-0"
          leave-to-class="translate-y-full"
          enter-active-class="transition duration-400 ease-out"
          leave-active-class="transition duration-400 ease-in"
        >
          <div
            v-if="isPopupExpanded || allActiveUploadsDone"
            class="flex flex-col gap-4 w-full min-h-0 overflow-auto"
          >
            <template
              v-for="(filesInGroup, componentType) in uploadsByComponent"
              :key="`file-uploader-progress-group-${componentType}`"
            >
              <label
                v-if="COMPONENT_GROUP_DISPLAY_NAMES.get(componentType)"
                class="text-xs text-gray-500 w-full text-start"
                :data-component-element-id="`file-uploader-progress-group-label-${componentType}`"
              >
                {{ COMPONENT_GROUP_DISPLAY_NAMES.get(componentType) }}
              </label>

              <div
                v-for="(file, fileIndex) in filesInGroup"
                :key="`file-uploader-progress-${fileIndex}`"
                :data-component-element-id="`file-uploader-progress-${fileIndex}`"
                class="flex flex-col gap-1"
              >
                <div class="flex items-center gap-2">
                  <BaseIconSvg
                    :name="file.displayIcon"
                    class="h-6! w-6! text-gray-400"
                    :data-component-element-id="`file-uploader-progress-icon-${fileIndex}`"
                  />

                  <span
                    class="text-xs text-gray-900 dark:text-white flex-1 text-start truncate"
                    :data-component-element-id="`file-uploader-progress-name-${fileIndex}`"
                  >
                    {{ file.displayName }}
                  </span>

                  <BaseIconSvg
                    v-if="file?.displayStatus === CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED"
                    name="flowbite:check-circle-solid"
                    :data-component-element-id="`file-uploader-progress-check-${fileIndex}`"
                    class="text-green-500 w-4! h-4! m-1"
                  />
                  <BaseIconSvg
                    v-else-if="file?.displayStatus === CONST.FILE_UPLOAD_ITEM_STATUS.FAILED"
                    name="flowbite:exclamation-circle-solid"
                    :data-component-element-id="`file-uploader-progress-check-${fileIndex}`"
                    class="text-red-500 w-4! h-4! m-1"
                  />
                </div>

                <FwbProgress
                  v-if="file?.percentage !== undefined && file?.percentage < 100 && ![CONST.FILE_UPLOAD_ITEM_STATUS.FINISHED, CONST.FILE_UPLOAD_ITEM_STATUS.FAILED].includes(file?.displayStatus)"
                  :data-component-element-id="`file-uploader-progress-bar-${fileIndex}`"
                  :progress="file.percentage"
                  size="sm"
                  class="px-1"
                />
              </div>
            </template>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@reference '#tailwind.scss';

.file-uploader-progress-container {
  ::-webkit-scrollbar {
    @apply w-3 h-2;
  }

  ::-webkit-scrollbar-track {
    border-top: 1px solid transparent;
    border-left: 1px solid transparent;
    @apply bg-transparent border-transparent border-l-[1px];
  }

  ::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full border-[3px] border-solid bg-clip-content border-transparent;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}
</style>
